import { Link } from 'react-router-dom'
import { Heart, Sparkles, Users, Award, Clock, Shield, Gift } from 'lucide-react'

const About = () => {
  return (
    <div className="spa-section">
      <div className="container-spa max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-800 mb-6">
            About Spa~Radise Luxe
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A soft, romantic sanctuary where spa rituals meet the comfort of your space. Relax, renew, and glow.
          </p>
        </div>

        {/* Our Story Section – romantic mauve/pink layout */}
        <div className="mb-16">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-50/95 via-pink-50/90 to-gray-50/95 border border-purple-100 shadow-xl">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-pink-200/30 to-transparent rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-purple-200/20 to-transparent rounded-full translate-y-1/2 -translate-x-1/2 pointer-events-none" />
            <div className="relative flex flex-col lg:flex-row items-start gap-10 p-8 md:p-12">
              {/* Logo */}
              <div className="lg:w-1/3 flex justify-center lg:justify-end">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-300 to-pink-400 rounded-2xl transform rotate-3 shadow-lg opacity-80" />
                  <div className="relative bg-white p-2 rounded-2xl shadow-xl border border-purple-100">
                    <img
                      src="/media/images/logo/lumoralogo.png"
                      alt="Spa~Radise Luxe"
                      className="w-56 h-56 md:w-64 md:h-64 object-contain rounded-xl p-4"
                    />
                  </div>
                </div>
              </div>

              {/* Our Story Content */}
              <div className="lg:w-2/3">
                <div className="mb-6">
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-800 mb-4">
                    Our Story
                  </h2>
                  <div className="w-20 h-1 bg-gradient-to-r from-purple-400 via-pink-400 to-rose-400 rounded-full mb-6" />
                </div>

                <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                  <p className="mb-6 text-lg">
                    Spa~Radise Luxe was created as a place where you can escape without leaving home. 
                    We blend soft lighting, calming music, and thoughtful details into every visit so you get 
                    a romantic, candlelit calm that feels made just for you.
                  </p>

                  <p className="mb-6 text-lg">
                    From soothing massages and glowing facials to body scrubs, waxing, manicures, and pedicures, 
                    our treatments are designed to rejuvenate your body and mind. Each session is tailored to 
                    your preferences and delivered with care in your own space.
                  </p>

                  <p className="mb-6 text-lg">
                    We believe wellness should feel personal and peaceful. Whether you book a single treatment 
                    or a combo package, our goal is the same: to leave you relaxed, refreshed, and glowing with 
                    the luxury of not having to step outside your door.
                  </p>

                  <p className="mb-8 text-lg">
                    Serving the greater area with the same attention to detail you’d expect from a high-end spa, 
                    we bring the full experience to you. Premium products, a serene setup, and a focus on you 
                    are at the heart of every Spa~Radise Luxe visit.
                  </p>
                </div>

                {/* Quote */}
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 border-l-4 border-pink-400 shadow-sm">
                  <blockquote className="text-lg italic text-gray-700 mb-4">
                    "Wellness should feel like a gentle escape: somewhere soft, safe, and just for you. 
                    At Spa~Radise Luxe we’re here to bring that feeling to your home, one ritual at a time."
                  </blockquote>
                  <cite className="text-sm font-semibold text-gray-600">
                    Spa~Radise Luxe
                  </cite>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Image between sections */}
        <div className="mb-16">
          <div className="max-w-4xl mx-auto rounded-3xl overflow-hidden shadow-xl border border-purple-100 bg-gray-100">
            <img
              src="/media/images/gallery/about1.jpeg"
              alt="Spa~Radise Luxe romantic spa setup"
              className="w-full h-72 md:h-80 object-cover"
            />
          </div>
        </div>

        {/* Mission, Vision, Values – mauve/pink/white cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="text-center p-8 bg-white rounded-2xl shadow-lg border border-purple-100 hover:shadow-xl hover:border-pink-100 transition-all duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center mx-auto mb-5 shadow-md">
              <Heart className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-serif font-bold text-gray-800 mb-3">Our Mission</h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              To offer personalised, romantic spa experiences at your doorstep so relaxation and glow 
              are always within reach.
            </p>
          </div>

          <div className="text-center p-8 bg-white rounded-2xl shadow-lg border border-pink-100 hover:shadow-xl hover:border-rose-100 transition-all duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-rose-400 rounded-full flex items-center justify-center mx-auto mb-5 shadow-md">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-serif font-bold text-gray-800 mb-3">Our Vision</h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              To be the go-to choice for at-home luxury spa, where quality, care, and a touch of 
              romance define every visit.
            </p>
          </div>

          <div className="text-center p-8 bg-white rounded-2xl shadow-lg border border-purple-100 hover:shadow-xl hover:border-pink-100 transition-all duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-300 to-pink-300 rounded-full flex items-center justify-center mx-auto mb-5 shadow-md">
              <Users className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-serif font-bold text-gray-800 mb-3">Our Values</h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              Care, professionalism, and attention to detail guide everything we do so you always 
              feel valued and pampered.
            </p>
          </div>
        </div>

        {/* Image between sections */}
        <div className="mb-16">
          <div className="max-w-4xl mx-auto rounded-3xl overflow-hidden shadow-xl border border-pink-100 bg-gray-100">
            <img
              src="/media/images/gallery/about2.jpeg"
              alt="Details from a Spa~Radise Luxe treatment"
              className="w-full h-72 md:h-80 object-cover"
            />
          </div>
        </div>

        {/* What We Offer – quick strip */}
        <div className="mb-16">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-center text-gray-800 mb-4">
            What We Offer
          </h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
            Six core services, plus combo packages tailored to you.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {['Massage', 'Facials', 'Wax', 'Body Scrubbing', 'Manicures', 'Pedicures', 'Combo Packages'].map((item) => (
              <Link
                key={item}
                to={item === 'Combo Packages' ? '/services?category=combo-packages' : `/services?category=${item.toLowerCase().replace(/\s+/g, '-')}`}
                className="px-5 py-2.5 rounded-full bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100 text-gray-700 font-medium hover:from-purple-100 hover:to-pink-100 hover:border-pink-200 transition-all duration-300"
              >
                {item}
              </Link>
            ))}
          </div>
        </div>

        {/* Image between sections */}
        <div className="mb-16">
          <div className="max-w-4xl mx-auto rounded-3xl overflow-hidden shadow-xl border border-purple-100 bg-gray-100">
            <img
              src="/media/images/gallery/about3.jpeg"
              alt="Soft lighting and spa bed ready for Spa~Radise session"
              className="w-full h-72 md:h-80 object-cover"
            />
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="mb-16">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-center text-gray-800 mb-4">
            Why Choose Spa~Radise Luxe?
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            We focus on the details that make your time with us special.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-purple-50 hover:border-pink-100 hover:shadow-md transition-all duration-300">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center flex-shrink-0 shadow">
                <Award className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Skilled & Caring</h3>
                <p className="text-gray-600">
                  Our therapists are trained and experienced, so you receive safe, professional 
                  treatments every time.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-purple-50 hover:border-pink-100 hover:shadow-md transition-all duration-300">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-rose-400 rounded-full flex items-center justify-center flex-shrink-0 shadow">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Flexible Scheduling</h3>
                <p className="text-gray-600">
                  We work around your schedule so you can enjoy your session at a time that suits you.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-purple-50 hover:border-pink-100 hover:shadow-md transition-all duration-300">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-300 to-pink-300 rounded-full flex items-center justify-center flex-shrink-0 shadow">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Quality Products</h3>
                <p className="text-gray-600">
                  We use quality, professional-grade products to care for your skin and body with care.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-purple-50 hover:border-pink-100 hover:shadow-md transition-all duration-300">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center flex-shrink-0 shadow">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Personalised Sessions</h3>
                <p className="text-gray-600">
                  Every treatment is tailored to your needs and preferences for a truly personal experience.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Image between sections */}
        <div className="mb-16">
          <div className="max-w-4xl mx-auto rounded-3xl overflow-hidden shadow-xl border border-pink-100 bg-gray-100">
            <img
              src="/media/images/gallery/about4.jpeg"
              alt="Spa~Radise Luxe experience captured in our gallery"
              className="w-full h-72 md:h-80 object-cover"
            />
          </div>
        </div>

        {/* Call to Action – romantic palette */}
        <div className="text-center rounded-3xl p-8 md:p-12 border border-purple-100 bg-gradient-to-br from-purple-50/90 via-pink-50/80 to-gray-50/90 shadow-xl">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-purple-300 to-pink-400 shadow-lg mb-6">
            <Gift className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-800 mb-4">
            Ready to Relax & Glow?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Book a session or treat someone special with a gift card. We’re here to bring 
            Spa~Radise Luxe to you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/services"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 via-pink-500 to-rose-400 hover:from-purple-600 hover:via-pink-600 hover:to-rose-500 text-white font-semibold py-3 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <span>View Our Services</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
            <Link
              to="/gift-card"
              className="inline-flex items-center justify-center gap-2 border-2 border-purple-200 text-purple-700 font-semibold py-3 px-8 rounded-xl hover:bg-purple-50 hover:border-purple-300 transition-all duration-300"
            >
              <Gift className="w-5 h-5" />
              <span>Gift Cards</span>
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 border-2 border-pink-200 text-pink-700 font-semibold py-3 px-8 rounded-xl hover:bg-pink-50 hover:border-pink-300 transition-all duration-300"
            >
              <span>Contact Us</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Final image – closing moment */}
        <div className="mt-16 mb-4">
          <div className="max-w-4xl mx-auto rounded-3xl overflow-hidden shadow-xl border border-purple-100 bg-gray-100">
            <img
              src="/media/images/gallery/about5.jpeg"
              alt="Final Spa~Radise Luxe moment"
              className="w-full h-72 md:h-80 object-cover"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default About
